import classnames from 'classnames'

import { DateRangeType } from '@/types'

type DatePickerDayProps = {
    value: Date | DateRangeType | null
    tempValue: Date | null
    hoveredValue: Date | null
    day: Date
    currentDate: Date
    singleMode?: boolean
    maxDate?: Date
    minDate?: Date
    'data-test'?: string
    onClick: (date: Date) => void
    onMouseEnter: () => void
    onMouseLeave: () => void
}

const isSingleModeFunc = (value: Date | DateRangeType | null): value is Date =>
    !Array.isArray(value)
const isRangeModeFunc = (value: Date | DateRangeType | null): value is DateRangeType =>
    Array.isArray(value)

export const DatePickerDay = ({
    day,
    currentDate,
    value,
    tempValue,
    hoveredValue,
    singleMode,
    maxDate,
    minDate,
    'data-test': dataTest,
    ...props
}: DatePickerDayProps) => {
    const isRangeFilled = isRangeModeFunc(value) && value[0] && value[1]
    const isSingleMode = isSingleModeFunc(value)
    const isRangeMode = isRangeModeFunc(value)
    const isDisabled = day.isAfter(maxDate) || day.isBefore(minDate)
    const isSelectedDate = (isSingleMode && day.isEqualTo(value)) ||
        (isRangeMode && tempValue && (day.isEqualTo(tempValue) || day.isEqualTo(hoveredValue))) ||
        (isRangeMode && !tempValue && (day.isEqualTo(value[0]) || day.isEqualTo(value[1])))
    const isInRange = isRangeMode && (
        (!tempValue && isRangeFilled && day.isAfter(value[0]) && day.isBefore(value[1])) ||
        (day.isBefore(tempValue) && day.isAfter(hoveredValue)) ||
        (day.isAfter(tempValue) && day.isBefore(hoveredValue)))
    const isStartDate = isRangeMode && (
        (!tempValue && isRangeFilled && day.isEqualTo(value[0]) && !day.isEqualTo(value[1])) ||
        (tempValue && hoveredValue && (
            (hoveredValue.isAfter(tempValue) && day.isEqualTo(tempValue)) ||
            (hoveredValue.isBefore(tempValue) && day.isEqualTo(hoveredValue))
        )))
    const isEndDate = isRangeMode && (
        (!tempValue && isRangeFilled && day.isEqualTo(value[1]) && !day.isEqualTo(value[0])) ||
        (tempValue && hoveredValue && (
            (hoveredValue.isAfter(tempValue) && day.isEqualTo(hoveredValue)) ||
            (hoveredValue.isBefore(tempValue) && day.isEqualTo(tempValue))
        )))


    return <div
        data-test={`${dataTest}${dataTest ? '-' : ''}day-wrapper-${day.toISODate()}`}
        className={classnames('relative transition-all',
            'after:block after:absolute after:-z-1 after:w-1/2 after:h-full after:right-0 after:top-0',
            'after:transition-all',
            'before:block before:absolute before:-z-1 before:w-1/2 before:h-full before:left-0 before:top-0',
            'before:transition-all', {
                invisible: day.getMonth() !== currentDate.getMonth(),
                'bg-gray-50': isInRange,
                'after:bg-gray-50': isStartDate,
                'before:bg-gray-50': isEndDate
            })}
    >
        <button
            disabled={isDisabled}
            onClick={() => props.onClick(day)}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            data-test={`${dataTest}${dataTest ? '-' : ''}day-${day.toISODate()}`}
            className={classnames(
                'w-10 h-10 flex justify-center items-center rounded-full',
                'border border-transparent relative transition-all duration-100',
                isDisabled ? 'text-gray-400' : 'cursor-pointer hover:border-primary-700',
                { 'bg-primary-700 text-white': isSelectedDate }
            )}
        >
            {day.getDate()}
            {day.isEqualTo(new Date()) && <div
                className="w-1 h-1 rounded-full bg-primary-500 absolute bottom-1"
            />}
        </button>
    </div>
}
